import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LeafIcon } from 'lucide-react';
import { ShopFaq } from '../shared/ShopFaq';
import { ImageSlot } from '../shared/ImageSlot';
import { GiftCardBlock } from '../shared/GiftCardBlock';
import { HeadingText } from '../shared/HeadingText';
import { RoutineCard } from '../shared/RoutineCard';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { TrustBadges } from '../shared/TrustBadges';
import { Drawer } from '../shared/Drawer';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { pageActions } from '../../data/pageActions';
import { usePages } from '../../contexts/SiteContext';
import { founder } from '../../data/content';
import { listNl, plural } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { btnPrimary, btnPrimaryLg, btnSecondary, btnSecondaryLg } from '../../ui/styles';
import { heroTitle, quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { FilterPanel } from './ShopFilters';
import { COLLECTION_ID, ShopCollection } from './ShopCollection';
import { IngredientIndex } from './IngredientIndex';
import { ROUTINES_SHOWN } from './shopRules';
import { placeholderImages } from '../../data/placeholderImages';
import { mediaOr } from '../../core/rules/media';
import { useShopFilters } from './useShopFilters';

/**
 * De webshop (docs/VORMGEVING.md › Pagina's). Vraag: wat past bij mijn huid, zonder te gokken?
 * Hoofdknop "Kies op je huidvraag" (naar de collectie met huidfilters), het huidadvies als tweede.
 * opening met beeld (sand) · collectie + beloftes (shell, schuift over de opening) · protocollen (sand) ·
 * wat er écht in zit (shell) · abonnement (klei, het ene moment) · merken (shell) · vragen (sand) · cadeaubon (blush).
 */
export function Shop() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    products,
    brands,
    routines
  } = useCatalog();
  const f = useShopFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const copy = pages.shop;
  const vars = {
    count: plural(products.length, 'product', 'producten'),
    brands: listNl(brands.map(b => b.name))
  };
  return <>
      {/* De opening als etalage: links wat je hier vindt en hoe je kiest, rechts een product in
          gebruik (W1). Op een telefoon zonder beeld: daar wil je zo snel mogelijk de producten zien.
          Extra ruimte onderaan: de collectie schuift er als licht vlak overheen. */}
      <section className="bg-sand" aria-labelledby="shop-title" data-id="element-7020" data-mp="2y4sqem9acwnw">
        <div className="mx-auto grid max-w-content gap-10 px-6 pb-14 pt-10 lg:grid-cols-12 lg:items-center lg:gap-16 lg:px-10 lg:pb-24 lg:pt-14" data-id="element-7021" data-mp="2y513of9bm3zm">
          <div className="lg:col-span-7" data-id="element-7022" data-mp="2o74tpqs4df33">
            <h1 id="shop-title" className={heroTitle} data-id="element-7023" data-mp="2o63jfoixgwmf">
              <HeadingText heading={copy.hero.heading} data-id="element-7024" data-mp="2j70bnwbkccar" />
            </h1>
            {copy.hero.body && <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-7025" data-mp="293tx0108gjht">{fill(copy.hero.body, vars)}</p>}
            {/* Hoofdknop: kiezen op je huid (de collectie met de huidfilters). Twijfel? De huidtest. */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-7026" data-mp="2j1hvn20ymb59">
              <a href={`#${COLLECTION_ID}`} className={btnPrimaryLg} data-id="element-7027" data-mp="2o0uelbopmy2k">
                {pageActions.chooseByConcern}
              </a>
              {copy.hero.secondaryCta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`${btnSecondaryLg} group`} data-id="element-7028" data-mp="1qazi172m0rdl">
                  {fill(copy.hero.secondaryCta)}
                  <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7029" data-mp="20ahz9fr4xz1y" />
                </button>}
            </div>
            {/* Vertrouwen, geen knop: haar gezicht bij de keuze, zoals de bylines op Salon en Over. */}
            {copy.proof && <p className="mt-8 flex items-center gap-3 text-[14px] text-muted" data-id="element-7030" data-mp="3eabnze5md73l">
                <img src={founder.portrait} alt="" width={40} height={40} className="h-10 w-10 shrink-0 rounded-full object-cover object-top" data-id="element-7031" data-mp="39bs21dr0zvb8" />
                {fill(copy.proof)}
              </p>}
          </div>
          <div className="hidden lg:col-span-5 lg:block" data-id="element-7032" data-mp="3j9fzibg0ngx3">
            <ImageSlot code="W1" priority media={mediaOr(copy.inUse.media, {
            image: placeholderImages.shopInUse,
            alt: 'Sfeerbeeld: een druppel serum op een vingertop, naast een open flesje'
          })} className="aspect-[5/4] w-full" sizes="(min-width: 1280px) 460px, 40vw" data-id="element-7033" data-mp="3j7v4kkpshvyp" />
          </div>
        </div>
      </section>

      {/* De collectie op een licht vlak dat over de opening schuift: de toonbank. De beloftes sluiten het
          vlak af: ze bevestigen de keuze, maar staan het kiezen niet in de weg. */}
      <ShopCollection f={f} onOpenFilters={() => setFiltersOpen(true)} footer={<TrustBadges data-id="element-7035" data-mp="n5f5kloyux9d" />} data-id="element-7034" data-mp="27o92rjwzqrjt" />

      {/* Protocollen direct na de collectie: wie twijfelt tussen losse producten, krijgt hier het complete plan. */}
      {routines.length > 0 && <Section tone="sand" labelledBy="shop-routines" contained={false} data-id="element-7036" data-mp="df3v0gas1pti">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7037" data-mp="idnjoogr9fpk">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7038" data-mp="ibudfaa20xi3">
              <div className="max-w-2xl" data-id="element-7039" data-mp="4d321vmktpxn">
                <h2 id="shop-routines" className={`${sectionHeading} text-ink`} data-id="element-7040" data-mp="4g8qd8ndsnsb">
                  <HeadingText heading={copy.routines.heading} data-id="element-7041" data-mp="9eabdz4dax6f" />
                </h2>
                {copy.routines.body && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-7042" data-mp="3qn730flvg9ey">{fill(copy.routines.body)}</p>}
              </div>
              {copy.routines.cta && <Link to="/routines" className={`${quietLink} text-ink`} data-id="element-7043" data-mp="1ov0zxdz0gkcd">
                  {fill(copy.routines.cta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7044" data-mp="1ts14tlk4jh1w" />
                </Link>}
            </div>

            <ul className="mt-14 grid gap-6 lg:grid-cols-3" data-id="element-7045" data-mp="1nc0zkjk2otp3">
              {routines.slice(0, ROUTINES_SHOWN).map(r => <li key={r.id} className="h-full bg-shell p-7 lg:p-8" data-id="element-7046" data-mp="1ieiqmt622eh3">
                  <RoutineCard routine={r} data-id="element-7047" data-mp="1ecdwltbp3ftx" />
                </li>)}
            </ul>
          </div>
        </Section>}

      {/* Wat er écht in zit: voor wie wil weten wáárom iets werkt. */}
      <IngredientIndex data-id="element-7048" data-mp="262s3v6p7dtwm" />

      <SubscriptionBlock data-id="element-7049" data-mp="264d2b7en1wzr" />

      {/* Merken */}
      <Section tone="shell" labelledBy="shop-brands" contained={false} data-id="element-7050" data-mp="21cbo3fxwtw73">
        <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7051" data-mp="21a8rue65z6tx">
          <div className="lg:col-span-5" data-id="element-7052" data-mp="1x8nhazh3r4mw">
            <h2 id="shop-brands" className={`${sectionHeading} text-ink`} data-id="element-7053" data-mp="2tt5vwx7sfsr7">
              {fill(copy.brands.heading)}
            </h2>
            <p className="mt-6 flex items-start gap-3 text-[17px] leading-relaxed text-muted" data-id="element-7054" data-mp="2p0cvgljcr6kx">
              <LeafIcon className="mt-1.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7055" data-mp="bkulqhsr064m" />
              {/* De boomclaim alleen zolang emerginC echt in het assortiment zit. */}
              {fill(copy.brands.body)}
              {brands.some(b => b.name === 'emerginC') && ' emerginC plant bovendien een boom voor elk verkocht product.'}
            </p>
          </div>
          <ul className="grid gap-10 sm:grid-cols-2 lg:col-span-7" data-id="element-7056" data-mp="uhclnmhr3cby">
            {brands.map(b => <li key={b.name} className="border-t border-ink/15 pt-6" data-id="element-7057" data-mp="zfxl3jp6hrzc">
                <h3 className="type-title text-ink" data-id="element-7058" data-mp="147p3v823etex">{b.name}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-7059" data-mp="14abczc4mtgz9">{b.description}</p>
              </li>)}
          </ul>
        </div>
      </Section>

      <ShopFaq data-id="element-7060" data-mp="1da6s1s1b5w4k" />

      <GiftCardBlock data-id="element-7061" data-mp="1d6iy4rryb7pb" />

      <Drawer open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters" footer={
    // Wissen zo smal als zijn woord, de hoofdknop krijgt de rest: zo past "Toon 43 producten"
    // ook op een telefoon van 320px, zonder afkappen of horizontaal scrollen.
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3" data-id="element-7063" data-mp="125r9m2msffy7">
            <button type="button" onClick={f.clear} disabled={!f.hasFilters} className={`${btnSecondary} px-4`} data-id="element-7064" data-mp="y3nzbhbuuhei">
              Wissen
            </button>
            {/* Bij nul resultaten niet "Toon 0 producten": dan is er niets te tonen. */}
            <button type="button" onClick={() => setFiltersOpen(false)} disabled={f.visible.length === 0} className={`${btnPrimary} min-w-0 px-4`} data-id="element-7065" data-mp="5g1jnapcrmba">
              {f.visible.length === 0 ? 'Geen producten' : `Toon ${plural(f.visible.length, 'product', 'producten')}`}
            </button>
          </div>} data-id="element-7062" data-mp="1i7gp9tuxvc2y">
        <FilterPanel skin={f.skin} setSkin={f.setSkin} step={f.step} setStep={f.setStep} brand={f.brand} setBrand={f.setBrand} showSkin={false} data-id="element-7066" data-mp="3mtxa4uleb0rr" />
      </Drawer>
    </>;
}